import { IcMandatory12 } from '@assets/icons/12x12';
import {
  Button,
  Center,
  createListCollection,
  Field as ChakraField,
  Fieldset,
  HStack,
  Icon,
  Input,
  Text,
  VStack,
} from '@chakra-ui/react';
import { CockpitFormLayout } from '@components/cockpit/layouts/CockpitFormLayout';
import Field from '@components/common/form/Field';
import ModifiedTag from '@components/common/form/ModifiedField';
import CustomInputDateTimeRange from '@components/common/inputs/CustomInputDateTimeRange';
import MultiSelect from '@components/ui/multiSelect/MultiSelect';
import RequiredIndicator from '@components/ui/RequiredIndicator';
import { useAppSelector } from '@hooks/redux.hooks';
import { hasRole } from '@redux/authent/authent.selectors';
import { selectSituationTime } from '@redux/situation/situation.selectors';
import { store } from '@redux/store';
import { selectDenormalizedDrones } from '@redux/utm/utm.selectors';
import { haveSameUniqueElements } from '@utils/common.utils';
import { hasChanged, isInvalid } from '@utils/validation/validation.utils';
import { useFormikContext } from 'formik';
import { FormattedMessage, useIntl } from 'react-intl';

import { RoleEnum } from '@/types/authent/roles.types';
import { DateTimeRange } from '@/types/dateTime.types';
import { ClassicFlightPlan } from '@/types/utm/flightPlan.types';

type Props = {
  setShowMapForm: (value: boolean) => void;
};

export default function FlightPlanGeneralForm({ setShowMapForm }: Readonly<Props>) {
  const { formatMessage } = useIntl();
  const drones = useAppSelector(selectDenormalizedDrones);
  const canSeeDrones = useAppSelector((state) => hasRole(state, RoleEnum.ACCESS_DRONE));
  const situationTime = selectSituationTime(store.getState());
  const { errors, touched, initialValues, values, setFieldValue, setTouched, validateField } =
    useFormikContext<ClassicFlightPlan>();

  return (
    <CockpitFormLayout title="components.flightPlans.formTitle">
      <Fieldset.Root invalid={Object.keys(errors).some((key) => touched[key as keyof typeof touched])}>
        <VStack width="100%" gap={5} alignItems="start">
          <ChakraField.Root variant="cockpit" required invalid={isInvalid('name', touched, errors)}>
            <ChakraField.Label width="100%" height="20px">
              <FormattedMessage id="components.flightPlans.name" />
              <RequiredIndicator />
              {hasChanged('name', initialValues, values) && <ModifiedTag />}
            </ChakraField.Label>
            <Field as={Input} type="text" name="name" id="name" />
          </ChakraField.Root>
          <ChakraField.Root variant="cockpit" required>
            <ChakraField.Label width="100%" height="20px">
              <FormattedMessage id="components.flightPlans.date" />
              <RequiredIndicator />
              {(hasChanged('startTime', initialValues, values) || hasChanged('endTime', initialValues, values)) && (
                <ModifiedTag />
              )}
            </ChakraField.Label>
            <Field
              as={CustomInputDateTimeRange}
              id="date-range"
              name="date-range"
              range={{
                to: values.endTime,
                from: values.startTime,
              }}
              minDate={situationTime}
              textColor="neutral.300"
              singleLine={false}
              showSecond={false}
              invalidFrom={isInvalid('startTime', touched, errors)}
              invalidTo={isInvalid('endTime', touched, errors)}
              onChange={async (range: DateTimeRange) => {
                setFieldValue('startTime', range.from);
                setFieldValue('endTime', range.to);
                await setTouched({ startTime: true, endTime: true });
                validateField('endTime');
              }}
            />
          </ChakraField.Root>
          {canSeeDrones && (
            <ChakraField.Root variant="cockpit" required>
              <ChakraField.Label width="100%" height="20px">
                <FormattedMessage id="components.flightPlans.drones" />
                <RequiredIndicator />
                {!haveSameUniqueElements(values.drones, initialValues.drones) && <ModifiedTag />}
              </ChakraField.Label>
              <MultiSelect
                values={values.drones.map((drone) => drone.code)}
                label="components.flightPlans.addDrone"
                collection={createListCollection({
                  items: drones.map((drone) => ({
                    label: drone.functionalName ?? formatMessage({ id: 'components.utm.drones.unknown' }),
                    value: drone.code,
                  })),
                })}
                onChange={(droneCodes) =>
                  setFieldValue(
                    'drones',
                    drones.filter((drone) => droneCodes.includes(drone.code)),
                  )
                }
              />
            </ChakraField.Root>
          )}
          <VStack gap={2} width="100%" alignItems="start">
            <HStack>
              <Text color="neutral.300">
                <FormattedMessage id="components.flightPlans.volumeDefinition" />
              </Text>
              <Icon as={IcMandatory12} boxSize="12px" />
            </HStack>

            <Center width="100%" padding={4} border="2px solid" borderColor="neutral.850">
              <Button variant="toggle" size="toggleM" flex={1} data-active onClick={() => setShowMapForm(true)}>
                <FormattedMessage id="components.flightPlans.draw" />
              </Button>
            </Center>
          </VStack>
        </VStack>
      </Fieldset.Root>
    </CockpitFormLayout>
  );
}
